import * as React from 'react';
import Transition from 'react-transition-group/Transition';
export interface TemperatureData {
    currentTemp?: number;
    weatherString: string;
    tempHigh: number;
    tempLow: number;
    precipitation: string;
}
export interface WeatherAnimationProps {
    locationIndex: number;
    styles: any;
    iconClass: any;
    attribution: string;
    temperatureData: TemperatureData;
}

export enum Direction {
    Right,
    Left,
}

export interface WeatherAnimationState<P extends WeatherAnimationProps> {
    animateIn: boolean;
    move: Direction;
    // By changing actual props externally, cachedProps will remain same to ensure that we continue to show old props until the Wrapped component is animated out
    // cachedProps will change only when one phase of animation(exiting out of the frame) is complete.
    // This will ensure that when the WrappedComponent animates in(enters the frame), then it shows updated props.
    cachedProps: P;
    componentError: boolean;
}

export interface SlideStyles {
    transitionProperty: string;
    willChange: string;
    opacity: number;
    transform: string;
    transition: string;
}

export enum AnimationStatus {
    EXITING = 'exiting',
    ENTERING = 'entering',
    EXITED = 'exited',
    ENTERED = 'entered',
}

const ANIMATION_DURATION: number = 70;
const ANIMATION_WIDTH: number = 20;
const EASING_EXIT: string = 'cubic-bezier(0.40, -0.00, 0.83, 1.17)';
const EASING_ENTER: string = 'cubic-bezier(0.17, 0.17, 0.00, 1.00)';

let currentStyles: SlideStyles = {
    transitionProperty: 'opacity, transform',
    willChange: 'opacity, transform',
    opacity: 1,
    transform: 'translate3d(0px 0px 0px)',
    transition: '0ms',
};

export default <P extends WeatherAnimationProps>(WrappedComponent: React.ComponentType<P>) => {
    return class WeatherAnimation extends React.Component<P, WeatherAnimationState<P>> {
        constructor(props: P) {
            super(props);
            let componentError = false;
            this.state = {
                animateIn: true,
                move: Direction.Right,
                // By changing actual props externally, cachedProps will remain same to ensure that we continue to show old props until the Wrapped component is animated out
                // cachedProps will change only when one phase of animation(exiting out of the frame) is complete.
                // This will ensure that when the WrappedComponent animates in(enters the frame), then it shows updated props.
                cachedProps: props,
                componentError,
            };
        }
        // before any render could occur, we are deciding the animation direction and setting up values to hide component with animation
        componentDidUpdate(prevProps: WeatherAnimationProps) {
            if (prevProps.locationIndex != this.props.locationIndex) {
                let move =
                    prevProps.locationIndex < this.props.locationIndex
                        ? Direction.Left
                        : Direction.Right;
                this.setState({ animateIn: false, move });
            }
        }
        // the incoming props are not passed to targeted component at once, rather it is passed when the component is about to enter with animation
        onEnter = (node: HTMLElement, isAppearing: boolean) => {
            this.setState({ cachedProps: this.props });
        };
        // callback used to start entering process of animation
        onExited = (node: HTMLElement) => {
            // set timeout is there because changing state at once caused rerender very soon which prohibited styles of EXITED animation state from being applied
            setTimeout(() => this.setState({ animateIn: true }), 20);
        };
        addEndListener = (node: HTMLElement, done: any) => {
            // use the css transitionend event to mark the finish of a transition
            node.addEventListener('transitionend', done, false);
        };
        render() {
            return this.state.componentError ? (
                <WrappedComponent {...this.props} />
            ) : (
                <Transition
                    in={this.state.animateIn}
                    onEnter={this.onEnter}
                    addEndListener={this.addEndListener}
                    onExited={this.onExited}
                    timeout={ANIMATION_DURATION}>
                    {(status: AnimationStatus) => {
                        // below logic is to apply transition duration and easing curve
                        if (
                            status === AnimationStatus.EXITING ||
                            status === AnimationStatus.ENTERED
                        ) {
                            currentStyles.transition = `${ANIMATION_DURATION}ms ${EASING_EXIT}`;
                        } else if (status === AnimationStatus.ENTERING) {
                            currentStyles.transition = `${
                                ANIMATION_DURATION * 2
                            }ms ${EASING_ENTER}`;
                        }

                        if (status === AnimationStatus.EXITED) {
                            currentStyles.transition = '0ms linear';
                            currentStyles.opacity = 0;
                        }

                        // below logic is for applying opacity
                        if (status === AnimationStatus.ENTERING) {
                            currentStyles.opacity = 1;
                        } else if (status === AnimationStatus.EXITING) {
                            currentStyles.opacity = 0;
                        }

                        // below logic is to apply transform
                        if (status === AnimationStatus.ENTERING) {
                            currentStyles.transform = 'translate3d(0, 0, 0)';
                        }
                        if (this.state.move === Direction.Right) {
                            if (status === AnimationStatus.EXITING) {
                                currentStyles.transform = `translate3d(${ANIMATION_WIDTH}px, 0, 0)`;
                            } else if (status === AnimationStatus.EXITED) {
                                currentStyles.transform = `translate3d(-${ANIMATION_WIDTH}px, 0, 0)`;
                            }
                        }
                        if (this.state.move === Direction.Left) {
                            if (status === AnimationStatus.EXITING) {
                                currentStyles.transform = `translate3d(-${ANIMATION_WIDTH}px, 0, 0)`;
                            } else if (status === AnimationStatus.EXITED) {
                                currentStyles.transform = `translate3d(${ANIMATION_WIDTH}px, 0, 0)`;
                            }
                        }
                        return (
                            <div style={{ ...currentStyles }}>
                                {this.state.cachedProps ? (
                                    <WrappedComponent {...this.state.cachedProps} />
                                ) : null}
                            </div>
                        );
                    }}
                </Transition>
            );
        }
    };
};
